import { useState, useCallback } from 'react';
import { Bookmark, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  MEME_TEMPLATES,
  TEMPLATE_CATEGORIES,
  getSavedTemplates,
  saveTemplate,
  deleteTemplate,
} from './utils/templates';
import { applyTemplate } from './utils/apply-template';

/**
 * Template picker panel — browse and apply meme templates.
 * Includes save-to-personal and delete personal templates.
 */
export const TemplatePanel = ({
  fabricRef,
  onModified,
  onBackgroundChange,
  onAspectChange,
  isDark,
}) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [savedTemplates, setSavedTemplates] = useState(() => getSavedTemplates());

  const handleApplyTemplate = useCallback(async (template) => {
    try {
      await applyTemplate(fabricRef.current, template, {
        onAspectChange,
        onBackgroundChange,
        onModified,
      });
      toast.success('Template applied');
    } catch {
      toast.error('Failed to load template');
    }
  }, [fabricRef, onModified, onAspectChange, onBackgroundChange]);

  const handleSave = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const name = window.prompt('Name your template:');
    if (!name) return;

    const json = canvas.toJSON();
    const newTemplate = saveTemplate(name, json, '1:1', { type: 'solid', value: '#000000' });
    setSavedTemplates(prev => [...prev, newTemplate]);
    toast.success('Template saved!');
  }, [fabricRef]);

  const handleDelete = useCallback((key) => {
    deleteTemplate(key);
    setSavedTemplates(prev => prev.filter(t => t.key !== key));
    toast.success('Template deleted');
  }, []);

  const mutedClass = isDark ? 'text-white/40' : 'text-gray-400';
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const borderClass = isDark ? 'border-white/10' : 'border-gray-200';
  const activeBg = isDark ? 'bg-white/10' : 'bg-gray-100';
  const hoverBg = isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50';

  const allTemplates = activeCategory === 'saved'
    ? savedTemplates
    : activeCategory === 'all'
      ? MEME_TEMPLATES
      : MEME_TEMPLATES.filter(t => t.category === activeCategory);

  return (
    <div className="p-3 space-y-2.5">
      {/* Save + Category row */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleSave}
          className={cn(
            "flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-display tracking-wider uppercase border rounded-sm transition-colors shrink-0",
            borderClass, mutedClass, hoverBg,
          )}
        >
          <Bookmark className="h-3.5 w-3.5" />
          Save
        </button>
        <div className={cn("flex gap-1 overflow-x-auto flex-1")}>
          {TEMPLATE_CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={cn(
                "px-2.5 py-1.5 text-[11px] font-display tracking-wider uppercase whitespace-nowrap rounded-sm transition-colors shrink-0",
                activeCategory === cat.key ? cn(textClass, activeBg) : mutedClass,
              )}
            >
              {cat.label}
              {cat.key === 'saved' && savedTemplates.length > 0 && (
                <span className="ml-1 text-amber-500">{savedTemplates.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Template grid — 4 columns, compact */}
      <div className="grid grid-cols-4 gap-1.5 max-h-36 overflow-y-auto">
        {allTemplates.length === 0 ? (
          <div className={cn("col-span-4 text-center py-4 text-xs", mutedClass)}>
            {activeCategory === 'saved' ? 'No saved templates yet' : 'No templates in this category'}
          </div>
        ) : (
          allTemplates.map((template) => (
            <div key={template.key} className="relative group">
              <button
                onClick={() => handleApplyTemplate(template)}
                className={cn(
                  "w-full flex flex-col items-center gap-0.5 p-1.5 border rounded-sm transition-colors",
                  borderClass, hoverBg
                )}
              >
                <div
                  className="w-full aspect-square rounded-sm flex items-center justify-center overflow-hidden text-[9px] leading-tight text-center px-1"
                  style={{
                    background: template.background?.type === 'gradient'
                      ? template.background.value
                      : template.background?.value || '#000',
                  }}
                >
                  <span style={{
                    color: template.layers?.[0]?.fill || '#FFF',
                    fontFamily: `"${template.layers?.[0]?.fontFamily || 'Inter'}", sans-serif`,
                    fontWeight: template.layers?.[0]?.fontWeight || '400',
                  }}>
                    {template.layers?.[0]?.text?.slice(0, 16) || 'Blank'}
                  </span>
                </div>
                <span className={cn("text-[10px] font-display tracking-wider uppercase truncate w-full text-center", mutedClass)}>
                  {template.label}
                </span>
              </button>
              {template.category === 'saved' && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(template.key); }}
                  className="absolute -top-1 -right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="h-2.5 w-2.5 text-white" />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
